import { FC, ReactNode } from 'react';
import history from 'service/customHistory';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import HelpIcon from '@mui/icons-material/Help';

import Button, { ButtonVariant } from 'components/common/Button/Button';

import './PageHeader.scss';
import ELEMENT from 'common/element';

/**
 * PageHeader contains a title and an optional Help button.
 *
 * @param {string} title      page title
 * @param {string} helpLink   URL for associated help page
 */

export type ButtonConfig = {
  icon?: JSX.Element;
  name?: string;
  link?: string;
  onClick?: () => void;
  variant?: ButtonVariant;
  disabled?: boolean;
  openInNewTab?: boolean;
  children?: ReactNode;
  testId?: ELEMENT;
};

type PageHeaderProps = {
  title: string;
  helpLink?: string;
  additionalRightButtons?: Array<ButtonConfig>;
  additionalLeftButtons?: Array<ButtonConfig>;
  hasBackButton?: boolean;
};

type ExtraButtonsProps = {
  additionalButtons?: Array<ButtonConfig>;
};

const ExtraButtons: FC<ExtraButtonsProps> = ({ additionalButtons }: ExtraButtonsProps) => (
  <>
    {additionalButtons &&
      additionalButtons.map(({ name, icon, link, onClick, variant, disabled, openInNewTab, children, testId }) => {
        if (children) {
          return children;
        }
        return (
          <Button
            href={link}
            leftIcon={icon}
            key={name}
            onClick={onClick}
            variant={variant}
            disabled={disabled}
            openInNewTab={openInNewTab}
            testId={testId}
          >
            {name}
          </Button>
        );
      })}
  </>
);

function PageHeader({
  title,
  helpLink,
  hasBackButton,
  additionalRightButtons,
  additionalLeftButtons,
}: PageHeaderProps): JSX.Element {
  return (
    <div className="PageHeader" data-test-id={ELEMENT.pageHeader}>
      <div className="PageHeader__TitleContainer">
        {hasBackButton && (
          <div
            role="button"
            tabIndex={0}
            className="PageHeader__LinkButton"
            onClick={history?.back}
            data-test-id={ELEMENT.backButton}
          >
            <ArrowBackIosIcon />
          </div>
        )}
        <span className="PageHeader__Title" data-test-id={ELEMENT.pageHeaderTitle}>
          {title}
        </span>
      </div>
      <div className="PageHeader__Buttons">
        <ExtraButtons additionalButtons={additionalLeftButtons} />
        {helpLink && (
          <Button
            key="helpLink"
            href={helpLink}
            leftIcon={<HelpIcon />}
            openInNewTab
            data-test-id={ELEMENT.helpButton}
            testId={ELEMENT.helpButton}
          >
            Help
          </Button>
        )}
        <ExtraButtons additionalButtons={additionalRightButtons} />
      </div>
    </div>
  );
}

export default PageHeader;
